import { type FC, type ReactNode } from "react";
import { cn } from "@heroui/react";
import { AnimatePresence, motion, type Transition } from "framer-motion";

const CARD = "rounded-2xl bg-app-surface/50 backdrop-blur-md border border-app-surface/80 shadow-lg p-4 text-app-text";

// Stable identities (module scope) so the floating panel doesn't re-create motion props each render.
const floatInitial = { opacity: 0, scale: 0.98 };
const floatAnimate = { opacity: 1, scale: 1 };
const floatExit = { opacity: 0, scale: 0.98 };
const floatTransition: Transition = { duration: 0.2 };

interface HomeWidgetShellProps {
    /** Extra root class (e.g. the widget name used as a CSS/theme hook). */
    className?: string;
    expanded: boolean;
    /** Width of the collapsed footprint that stays in flow (default `w-120`). */
    collapsedWidth?: string;
    /** Width of the floating expanded panel, e.g. `w-[1120px]`. */
    expandedWidth: string;
    /**
     * Which edge the expanded panel is pinned to. `"right"` (default) grows the
     * panel leftward - correct for the rightmost widget column. A widget in a
     * column further left must use `"left"`, or a wide panel growing leftward
     * runs off the viewport (the container does not clip).
     */
    anchor?: "left" | "right";
    /** Header row - rendered in both layers (title area + actions + toggle). */
    header: ReactNode;
    /** Optional always-visible content directly under the header (e.g. a summary line). */
    subHeader?: ReactNode;
    /** Collapsed body. */
    collapsed: ReactNode;
    /** Expanded body - floats. */
    children: ReactNode;
    /** Optional always-mounted extras that are not part of the card (e.g. a modal). */
    extras?: ReactNode;
}

/**
 * Home tile that expands as a floating overlay instead of reflowing the column.
 *
 * The collapsed card stays in flow and defines the tile's footprint, so sibling
 * tiles never move. When expanded, the panel is rendered a second time as an
 * absolutely-positioned layer anchored to the tile's top-right corner, growing
 * left and down over the tiles below (z-40). Nothing leaves the screen sideways:
 * the right edge is pinned and the panel is capped to the viewport.
 */
export const HomeWidgetShell: FC<HomeWidgetShellProps> = ({
    className,
    expanded,
    collapsedWidth = "w-120",
    expandedWidth,
    anchor = "right",
    header,
    subHeader,
    collapsed,
    children,
    extras,
}) => (
    <div className={cn("relative max-w-[calc(100vw-3rem)]", collapsedWidth, className)}>
        {/* Base card - holds the footprint in flow; hidden (but space-preserving) while expanded.
            `inert` rather than `aria-hidden`: the toggle that expands the tile lives in this card
            and still owns focus at the moment the card is hidden, and Chrome refuses `aria-hidden`
            on a subtree that retains focus. `inert` hides it from assistive tech *and* drops it out
            of the focus order. */}
        <div className={cn(CARD, "relative w-full", expanded && "invisible")} inert={expanded}>
            {header}
            {subHeader}
            {collapsed}
        </div>

        {/* Floating expanded panel - anchored top-right, grows left + down over the siblings. */}
        <AnimatePresence>
            {expanded && (
                <motion.div
                    key="expanded"
                    initial={floatInitial}
                    animate={floatAnimate}
                    exit={floatExit}
                    transition={floatTransition}
                    className={cn(
                        CARD,
                        "absolute top-0 z-40 max-h-[calc(100vh-6rem)] max-w-[calc(100vw-3rem)] overflow-y-auto",
                        anchor === "right" ? "right-0 origin-top-right" : "left-0 origin-top-left",
                        expandedWidth,
                    )}
                >
                    {header}
                    {subHeader}
                    {children}
                </motion.div>
            )}
        </AnimatePresence>

        {extras}
    </div>
);
